import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _ from 'lodash';
import moment from 'moment';
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  Chip,
} from '@material-ui/core';
import { DatePicker } from 'material-ui-pickers';

const STRING_TYPES = ['string', 'number', 'phone', 'email'];

class Filter extends React.Component {
  static propTypes = {
    initialValues: PropTypes.object,
    config: PropTypes.array,
    renderFilterActions: PropTypes.func,
    onFilterClear: PropTypes.func,
  };

  static defaultProps = {
    initialValues: {},
    config: [],
    renderFilterActions: () => {},
    onFilterClear: () => {},
  };

  constructor(props) {
    super(props);

    this.state = this.getInitialState();
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.config, this.props.config)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.getInitialState());
    }
  }

  getInitialState = () => {
    const { initialValues, config } = this.props;
    const filters = config.reduce((acc, filter) => {
      if (STRING_TYPES.includes(filter.type) || filter.type === 'select') {
        return {
          ...acc,
          [filter.id]: {
            value: initialValues[filter.id] ? initialValues[filter.id].value : '',
            lookupType: initialValues[filter.id] ? initialValues[filter.id].lookupType : 'equal',
          },
        };
      }
      if (filter.type === 'multiselect') {
        return {
          ...acc,
          [filter.id]: {
            value: initialValues[filter.id] ? initialValues[filter.id].value : [],
            lookupType: initialValues[filter.id] ? initialValues[filter.id].lookupType : 'equal',
          },
        };
      }
      return {
        ...acc,
        [filter.id]: {
          value: initialValues[filter.id] ? initialValues[filter.id].value : null,
          lookupType: initialValues[filter.id] ? initialValues[filter.id].lookupType : 'equal',
        },
      };
    }, {});

    return filters;
  };

  getTextInputType = (fieldType) => {
    if (fieldType === 'number') {
      return 'number';
    }
    return 'text';
  };

  clearFilter = () => {
    const filters = this.props.config.reduce((acc, filter) => {
      if (STRING_TYPES.includes(filter.type) || filter.type === 'select') {
        return {
          ...acc,
          [filter.id]: {
            value: '',
            lookupType: 'equal',
          },
        };
      }
      if (filter.type === 'multiselect') {
        return {
          ...acc,
          [filter.id]: {
            value: [],
            lookupType: 'equal',
          },
        };
      }
      return {
        ...acc,
        [filter.id]: {
          value: null,
          lookupType: 'equal',
        },
      };
    }, {});

    this.setState(filters, this.props.onFilterClear);
  };

  generateFilterField = (filterData) => {
    let fieldComponent = null;

    if (STRING_TYPES.includes(filterData.type)) {
      fieldComponent = (
        <StyledFormControl>
          <div>{filterData.label || ''}</div>
          {this.renderLookupTypes(filterData)}
          <TextField
            id={filterData.id}
            value={this.state[filterData.id].value}
            onChange={(event) => {
              const { value } = event.target;
              this.setState(state => ({
                [filterData.id]: {
                  ...state[filterData.id],
                  value,
                },
              }));
            }}
            type={this.getTextInputType(filterData.type)}
            fullWidth
          />
        </StyledFormControl>
      );
    } else if (filterData.type === 'select') {
      fieldComponent = (
        <StyledFormControl>
          <div>{filterData.label || ''}</div>
          {this.renderLookupTypes(filterData)}
          <Select
            value={this.state[filterData.id].value}
            onChange={(event) => {
              const { value } = event.target;
              this.setState(state => ({
                [filterData.id]: {
                  ...state[filterData.id],
                  value,
                },
              }));
            }}
            inputProps={{
              name: filterData.id,
              id: filterData.id,
            }}
            autoWidth
          >
            {filterData.choices
              && filterData.choices.map(choice => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.label}
                </MenuItem>
              ))}
          </Select>
        </StyledFormControl>
      );
    } else if (filterData.type === 'multiselect') {
      fieldComponent = (
        <StyledFormControl>
          <div>{filterData.label || ''}</div>
          {this.renderLookupTypes(filterData)}
          <Select
            value={this.state[filterData.id].value}
            onChange={(event) => {
              const { value } = event.target;
              this.setState(state => ({
                [filterData.id]: {
                  ...state[filterData.id],
                  value,
                },
              }));
            }}
            inputProps={{
              name: filterData.id,
              id: filterData.id,
            }}
            renderValue={selected => (
              <SelectedChipsContainer>
                {selected.map(value => (
                  <Chip key={value} label={_.filter(filterData.choices, { value })[0].label} />
                ))}
              </SelectedChipsContainer>
            )}
            autoWidth
            multiple
          >
            {filterData.choices
              && filterData.choices.map(choice => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.label}
                </MenuItem>
              ))}
          </Select>
        </StyledFormControl>
      );
    } else if (filterData.type === 'date') {
      fieldComponent = (
        <StyledFormControl>
          <div>{filterData.label || ''}</div>
          {this.renderLookupTypes(filterData)}
          <StyledDatePicker
            clearable
            value={
              this.state[filterData.id].value
                ? moment(this.state[filterData.id].value, 'DD-MM-YYYY')
                : null
            }
            format="DD-MM-YYYY"
            onChange={(date) => {
              this.setState(state => ({
                [filterData.id]: {
                  ...state[filterData.id],
                  value: moment(date).format('DD-MM-YYYY'),
                },
              }));
            }}
          />
        </StyledFormControl>
      );
    }

    return fieldComponent;
  };

  computeFilterData = () => {
    const filterData = {};
    Object.keys(this.state).forEach((filterKey) => {
      const keyData = this.state[filterKey];
      if (keyData.value && keyData.value.length !== 0) {
        filterData[filterKey] = keyData;
      }
    });

    return filterData;
  };

  renderLookupTypes = (filterData) => {
    if (filterData.lookupType.length > 1) {
      const getLabel = (choice) => {
        if (choice === 'geq') {
          return 'Greater than';
        }
        if (choice === 'leq') {
          return 'Less than';
        }
        return 'Equal to';
      };

      return (
        <StyledRadioGroup
          aria-label={filterData.id}
          name={filterData.id}
          value={this.state[filterData.id].lookupType}
          onChange={(event) => {
            const { value } = event.target;
            this.setState(state => ({
              [filterData.id]: {
                ...state[filterData.id],
                lookupType: value,
              },
            }));
          }}
        >
          {filterData.lookupType.map(choice => (
            <FormControlLabel
              key={choice}
              value={choice}
              control={<Radio color="primary" />}
              label={getLabel(choice)}
            />
          ))}
        </StyledRadioGroup>
      );
    }

    return null;
  };

  render() {
    const { config, renderFilterActions } = this.props;

    return (
      <div>
        <Typography variant="headline" gutterBottom>
          Filter
        </Typography>

        <InputsContainer>
          {config
            && config.map(filterData => (
              <div key={filterData.id}>{this.generateFilterField(filterData)}</div>
            ))}
        </InputsContainer>

        {renderFilterActions({
          filterData: this.computeFilterData(),
          clearFilter: this.clearFilter,
        })}
      </div>
    );
  }
}

export default Filter;

const InputsContainer = styled.div`
  margin-bottom: 30px;

  & > * + * {
    margin-top: 15px !important;
  }
`;

const StyledFormControl = styled.div`
  width: 100%;

  & > div {
    width: 100%;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex !important;
  align-items: center !important;
  flex-direction: row !important;
`;

const SelectedChipsContainer = styled.div`
  & > * + * {
    margin-left: 5px;
  }
`;



// WEBPACK FOOTER //
// ./src/components/Filter/Filter.js